<template>
  <div class="news-detail">
    <!-- 顶部标题区域 -->
    <section class="products-header">
      <div class="container">
        <h1 class="page-title">
          <span class="title-glow">新闻详情</span>
        </h1>
        <p class="page-subtitle">了解最新行业动态与公司资讯</p>
      </div>
    </section>

    <!-- 正文内容 -->
    <section class="detail-content">
      <div class="container">
        <!-- 加载状态 -->
        <div v-if="loading" class="detail-loading">
          <div class="loading-spinner"></div>
          <p>加载中...</p>
        </div>
        <!-- 错误提示 -->
        <div v-else-if="error" class="detail-error">
          <p>{{ error }}</p>
          <p class="error-hint">请确保 Node.js 服务器正在运行（端口 3000）</p>
        </div>
        <!-- 新闻详情内容 -->
        <template v-else-if="newsDetail">
          <!-- 正文内标题与信息 -->
          <h2 class="content-title">{{ pageTitle }}</h2>
          <!-- 新闻摘要 -->
          <p v-if="pageSummary" class="news-summary">{{ pageSummary }}</p>
          <div class="content-meta">
            <span>时间：{{ pageDate }}</span>
            <span class="dot">·</span>
            <span>来源：{{ pageSource || pageFrom }}</span>
          </div>

          <!-- 新闻图片 -->
          <div class="detail-image" v-if="pageImage">
            <img :src="resolveDetailImage(pageImage)" :alt="pageTitle" />
            <div class="image-caption">{{ pageImageCaption }}</div>
          </div>

          <!-- 新闻正文 -->
          <div v-if="pageBody && pageBody.length > 0" class="news-body">
            <p v-for="(paragraph, index) in pageBody" :key="index">{{ paragraph }}</p>
          </div>

          <div class="detail-footer">
            <router-link to="/news" class="back-link">返回列表</router-link>
          </div>

          <!-- 上一篇 / 下一篇 -->
          <div class="prev-next">
            <div class="pn-item">
              <span class="pn-label">上一篇：</span>
              <template v-if="prevTitle">
                <router-link :to="prevRoute" class="pn-link">{{ prevTitle }}</router-link>
              </template>
              <template v-else>
                <span class="pn-none">暂无</span>
              </template>
            </div>
            <div class="pn-item">
              <span class="pn-label">下一篇：</span>
              <template v-if="nextTitle">
                <router-link :to="nextRoute" class="pn-link">{{ nextTitle }}</router-link>
              </template>
              <template v-else>
                <span class="pn-none">暂无</span>
              </template>
            </div>
          </div>
        </template>
      </div>
    </section>
  </div>
  </template>

<script>
import axios from 'axios'

// 使用 require.context 加载新闻详情本地图片（支持 jpg 和 png）
const newsDetailImageCtx = require.context('@/assets/news_photo/newsdetail_photo', false, /\.(png|jpe?g)$/)

export default {
  name: 'NewsDetail',
  data() {
    return {
      newsDetail: null,
      prevNews: null,
      nextNews: null,
      loading: false,
      error: null
    }
  },
  computed: {
    pageTitle() {
      return this.newsDetail?.title || ''
    },
    pageDate() {
      return this.newsDetail?.date || ''
    },
    pageFrom() {
      return this.newsDetail?.category || ''
    },
    pageSource() {
      return this.newsDetail?.source || ''
    },
    pageImage() {
      return this.newsDetail?.image || ''
    },
    pageImageCaption() {
      if (this.newsDetail?.imageCaption) {
        return this.newsDetail.imageCaption
      }
      return this.pageTitle || '新闻配图'
    },
    pageSummary() {
      return this.newsDetail?.summary || ''
    },
    pageBody() {
      return this.newsDetail?.body || []
    },
    prevTitle() {
      return this.prevNews?.title || ''
    },
    nextTitle() {
      return this.nextNews?.title || ''
    },
    prevRoute() {
      if (!this.prevNews || !this.prevNews.id) return {}
      return {
        path: '/news/detail',
        query: {
          id: this.prevNews.id
        }
      }
    },
    nextRoute() {
      if (!this.nextNews || !this.nextNews.id) return {}
      return {
        path: '/news/detail',
        query: {
          id: this.nextNews.id
        }
      }
    }
  },
  async mounted() {
    await this.loadFromRoute()
  },
  watch: {
    '$route.query.id'() {
      // 路由 query 中的 id 变化时，重新加载当前详情和上一篇/下一篇
      this.loadFromRoute()
    }
  },
  methods: {
    async loadFromRoute() {
      const newsId = this.$route.query.id
      if (!newsId) {
        this.error = '缺少新闻ID参数'
        this.loading = false
        return
      }

      // 重置相邻新闻
      this.prevNews = null
      this.nextNews = null

      await this.fetchNewsDetail(newsId)
      await this.fetchPrevNextFromList(newsId)
    },
    async fetchNewsDetail(newsId) {
      try {
        this.loading = true
        this.error = null
        const response = await axios.get(`/api/news/detail/${newsId}`)
        if (response.data.code === 200) {
          this.newsDetail = response.data.data
        } else {
          this.error = response.data.message || '获取新闻详情失败'
          this.newsDetail = null
        }
      } catch (error) {
        console.error('获取新闻详情失败:', error)
        if (error.response && error.response.status === 404) {
          this.error = '新闻详情不存在'
        } else {
          this.error = '网络请求失败，请检查服务器是否运行'
        }
        this.newsDetail = null
      } finally {
        this.loading = false
      }
    },
    async fetchPrevNextFromList(currentId) {
      try {
        const response = await axios.get('/api/news/list')
        if (response.data.code !== 200) {
          return
        }

        const resData = response.data.data
        let list = []

        if (Array.isArray(resData)) {
          // API 模式：后端已返回扁平数组，并带有 type 字段
          list = resData
        } else if (resData && typeof resData === 'object') {
          // 静态 JSON：company / industry 两个分组
          const companyNews = Array.isArray(resData.company)
            ? resData.company.map(item => ({ ...item, type: item.type || 'company' }))
            : []
          const industryNews = Array.isArray(resData.industry)
            ? resData.industry.map(item => ({ ...item, type: item.type || 'industry' }))
            : []
          list = [...companyNews, ...industryNews]
        }

        if (!Array.isArray(list) || list.length === 0) return

        const current = list.find(n => String(n.id) === String(currentId))
        if (!current) return

        const getType = (item) => item.type || (item.category === '公司新闻' ? 'company' : 'industry')
        const currentType = getType(current)

        const sameList = list.filter(item => getType(item) === currentType)
        const idx = sameList.findIndex(n => String(n.id) === String(currentId))
        if (idx === -1) return

        this.prevNews = idx > 0 ? sameList[idx - 1] : null
        this.nextNews = idx < sameList.length - 1 ? sameList[idx + 1] : null
      } catch (error) {
        console.error('根据新闻列表计算上一篇/下一篇失败:', error)
      }
    },
    resolveDetailImage(imagePath) {
      if (!imagePath) return ''
      if (imagePath.startsWith('@/assets/news_photo/newsdetail_photo/')) {
        const fileName = imagePath.split('/').pop()
        try {
          return newsDetailImageCtx('./' + fileName)
        } catch (e) {
          console.warn('新闻详情图片未找到:', fileName, e)
          return ''
        }
      }
      // 其他情况（远程 URL 等）直接返回
      return imagePath
    }
  }
}
</script>

<style scoped>
.news-detail {
  min-height: 100vh;
  /* padding-top: 70px; */
  background: #f5f7fa;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 顶部标题区域 */
.products-header {
  position: relative;
  background-image: url('@/assets/background_header/新闻中心详情页.png');
  background-size: cover;
  background-position: center;
  padding: 45px 0;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
}

.products-header .container {
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0;
  padding: 0 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
}

.page-title {
  position: relative;
  z-index: 2;
  margin: 0;
  text-align: left;
  padding: 0;
}

.page-subtitle {
  margin: 8px 0 0 0;
  text-align: left;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.8;
  opacity: 1;
  max-width: 900px;
  margin-left: 2em;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(5px);
}

.title-glow {
  display: inline-block;
  font-size: 46px;
  font-weight: 900;
  letter-spacing: 5px;
  color: #ffffff;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.6));
  position: relative;
  backdrop-filter: blur(5px);
}

.detail-meta {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-meta .dot {
  opacity: 0.7;
}

.detail-content {
  padding: 28px 0 60px 0;
}

.content-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.6;
  text-align: center;
}

.content-meta {
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.content-meta .dot {
  color: #aaa;
}

.detail-content p {
  font-size: 16px;
  color: #3a3a3a;
  line-height: 2;
  margin: 18px 0;
  text-indent: 2em;
}

.detail-image {
  margin: 26px 0;
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detail-image img {
  width: 80%;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.image-caption {
  display: inline-block;
  margin-top: 8px;
  color: #888;
  font-size: 13px;
  text-align: center;
  width: 100%;
}

.detail-footer {
  margin-top: 36px;
  display: flex;
  justify-content: flex-end;
}

.back-link {
  color: #4c95e3;
  text-decoration: none;
  border: 1px solid #4c95e3;
  padding: 8px 14px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.back-link:hover {
  background: #4c95e3;
  color: #fff;
}

.prev-next {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.pn-item {
  color: #444;
  font-size: 14px;
}

.pn-label {
  color: #888;
  margin-right: 6px;
}

.pn-link {
  color: #4c95e3;
  text-decoration: none;
}

.pn-link:hover {
  text-decoration: underline;
}

.pn-none {
  color: #bbb;
}

.news-summary {
  font-size: 10px;
  line-height: 2;
  margin: 18px 0;
  color: #1a1a1a;
  font-weight: 500;
  text-align: right;
}

.news-highlights {
  margin: 24px 0;
}

.highlights-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px 0;
}

.highlights-list {
  margin: 0;
  padding-left: 25px;
  color: #3a3a3a;
  line-height: 2;
}

.highlights-list li {
  margin-bottom: 8px;
}

.news-body {
  margin-top: 24px;
}

/* 加载和错误状态 */
.detail-loading,
.detail-error {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4c95e3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.detail-loading p {
  color: #666;
  font-size: 16px;
}

.detail-error {
  background: #ffecec;
  border-radius: 8px;
  padding: 30px;
}

.detail-error p {
  color: #c0392b;
  margin: 0 0 10px 0;
  font-size: 16px;
}

.error-hint {
  color: #999 !important;
  font-size: 14px !important;
}

@media (max-width: 768px) {
  .detail-title {
    font-size: 22px;
  }

  .detail-content p {
    font-size: 15px;
  }

  .news-summary {
    font-size: 10px;
  }
}
</style>


